import { useState, useEffect } from "react";
import { Move } from "../../../types";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCloneMoveMutation } from "@/store/moves/apis";
import { cn } from "@/lib/utils";
import { MOVE_KINDS, SERVICE_TYPES } from "@/constants";
import { useNavigate } from "react-router-dom";

// type Payload = {
//   [key: string]: string | number | boolean | undefined | null | string[];
// };

const Clone = ({
  move,
  triggerChildren,
}: {
  move: Move;
  triggerChildren: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const basicFields = [
    "customer_name",
    "customer_id",
    "customer_email",
    "customer_mobile_phone",
    "customer_alternate_phone",
    "can_text_primary",
    "can_text_secondary",
  ];
  const [selectedFields, setSelectedFields] = useState<string[]>(basicFields);
  const [cloneMove] = useCloneMoveMutation();
  const extraFields = [
    "service",
    "service_date",
    "kind",
    "area",
    "num_of_rooms",
    "includes_garage",
    "num_of_movers",
    "num_of_trucks",
    "billing_type",
    "notes_customer",
    "notes_internal",
    "origin",
    "stops",
    "destination",
    "inventory_items",
    ...selectedFields,
  ];

  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (selectAll) {
      setSelectedFields(basicFields.concat(extraFields));
    } else {
      setSelectedFields(basicFields);
    }
  }, [selectAll]);

  const toggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const handleClone = async () => {
    // let payload: Payload = {};
    //  = selectedFields.reduce((acc, field) => {
    //   if (
    //     ["origin", "stops", "destination", "inventory_items"].includes(field)
    //   ) {
    //     acc[`${field}_attributes`] = move[field as keyof Move];
    //   } else {
    //     acc[field] = move[field as keyof Move];
    //   }
    //   return acc;
    // }, {});

    // payload["status"] = "initial";

    try {
      const newMove = await cloneMove({
        id: move.id,
        data: { clone_fields: selectedFields },
      }).unwrap();
      setIsOpen(false);
      navigate(`/move/${newMove.id}`);
    } catch (error) {
      console.error("Failed to clone move:", error);
    }
  };

  const renderField = (
    label: string,
    fields: string[],
    value: string | number | boolean | undefined | null | string[]
  ) => (
    <div key={fields.join("_")} className="flex items-center space-x-2 py-1">
      <Switch
        checked={fields.every((field) => selectedFields.includes(field))}
        onCheckedChange={() => {
          fields.forEach((field) => toggleField(field));
        }}
      />
      <span className="text-sm font-medium">{label}</span>
      <span className="text-sm text-gray-800">{value}</span>
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{triggerChildren}</SheetTrigger>
      <SheetContent
        side="right"
        className={cn(
          "w-[50vw] max-w-[50vw]",
          "sm:max-w-[50vw]",
          "lg:max-w-[50vw]"
        )}
      >
        <SheetHeader>
          <div className="w-full flex flex-row justify-start items-center gap-8">
            <SheetTitle>Clone Move</SheetTitle>
            <div className="flex flex-row items-center gap-2 mr-8">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleClone}>Clone</Button>
            </div>
          </div>
        </SheetHeader>
        <hr className="my-4" />
        <div className="mt-4">
          <p className="text-lg font-semibold">{move.customer_name}</p>
          <div className="text-sm text-gray-900 font-normal flex flex-row items-center gap-1">
            <span className="">{move.customer_email}</span>
            {"•"}
            <span className="">{move.customer_mobile_phone}</span>
            {"•"}
            <span className="">{move.customer_alternate_phone}</span>
          </div>
        </div>
        <div className="mt-6 space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="flex items-center space-x-2 py-2">
            <Switch checked={selectAll} onCheckedChange={setSelectAll} />
            <span className="text-sm font-medium">Select All</span>
          </div>
          {renderField(
            "Service",
            ["service"],
            SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] ||
              move.service ||
              "N/A"
          )}
          {renderField("Service Date", ["service_date"], move.service_date)}
          {renderField(
            "Details",
            ["kind", "area", "num_of_rooms", "includes_garage"],
            `${MOVE_KINDS[move.kind as keyof typeof MOVE_KINDS]} - ${
              move.area
            } sq.ft. - ${move.num_of_rooms}BR - ${
              move.includes_garage ? "Garage" : "No Garage"
            }`
          )}
          {renderField("Movers", ["num_of_movers"], move.num_of_movers)}
          {renderField("Trucks", ["num_of_trucks"], move.num_of_trucks)}
          {renderField("Billing", ["billing_type"], move.billing_type)}
          {renderField(
            "Notes (Customer)",
            ["notes_customer"],
            move.notes_customer
          )}
          {renderField(
            "Notes (Internal)",
            ["notes_internal"],
            move.notes_internal
          )}
          {renderField(
            "Origin",
            ["origin"],
            `${move.origin?.street}, ${move.origin?.city}, ${move.origin?.state} ${move.origin?.zipcode}`
          )}
          {renderField(
            "Stops",
            ["stops"],
            `${move.stops.map((stop) => `${stop.city}`).join(", ")}`
          )}
          {renderField(
            "Destination",
            ["destination"],
            `${move.destination?.street}, ${move.destination?.city}, ${move.destination?.state} ${move.destination?.zipcode}`
          )}
          {renderField(
            "Inventory Items",
            ["inventory_items"],
            `${move.inventory_items?.length || 0} items`
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Clone;
